/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React from "react";
import Select from "react-select";
import { createReservationTranslator } from "../reservations/reservationI18n";
import { guestProfileMessages, getGuestCountryLabel } from "../reservations/guestProfileI18n";
import { useEffect, useState } from "react";
import axios from "../api/axiosInstance"; // ✅ con token incluido


const CustomerForm = ({
  formData,
  handleChange,
  handleSelectChange,
  requiredFields = [],
  appearance = "default",
  customerSelectStyles = null,
  selectMenuPortalTarget = null,
  disabled = false,
  languageId = "en",
}) => {

  const t = React.useMemo(() => createReservationTranslator(languageId, guestProfileMessages), [languageId]);
  const selectLabels = { placeholder: t("Select"), noOptionsMessage: () => t("No options"), loadingMessage: () => t("Loading...") };
  const isFieldRequired = (fieldName) => requiredFields.includes(fieldName);
  const RequiredMark = ({ fieldName }) =>
    isFieldRequired(fieldName) ? <span className="text-danger fw-bold">*</span> : null;


  const countryList = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola",
    "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus",
    "Belgium", "Belize", "Benin", "Bhutan", "Bolivia",
    "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
    "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada",
    "Cape Verde", "Central African Republic", "Chad", "Chile", "China",
    "Colombia", "Comoros", "Costa Rica", "Croatia", "Cuba",
    "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica",
    "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Estonia",
    "Eswatini", "Ethiopia", "Fiji", "Finland", "France",
    "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
    "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau",
    "Guyana", "Haiti", "Honduras", "Hungary", "Iceland",
    "India", "Indonesia", "Iran", "Iraq", "Ireland",
    "Israel", "Italy", "Jamaica", "Japan", "Jordan",
    "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia",
    "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar",
    "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
    "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia",
    "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
    "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal",
    "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
    "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan",
    "Palau", "Panama", "Papua New Guinea", "Paraguay", "Peru",
    "Philippines", "Poland", "Portugal", "Qatar", "Romania",
    "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines",
    "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal",
    "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia",
    "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
    "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
    "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
    "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga",
    "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
    "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
    "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
    "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ];

  const [vipCategories, setVipCategories] = useState([]);
  const [setMaxAgeBaby] = useState("");      // 👶 Edad máxima de bebé
  const [maxAgeChild, setMaxAgeChild] = useState("");    // 🧒 Edad máxima de niño
  const [minAgeChild] = useState(null);
  const [reservation] = useState({});
  const [guests, setGuests] = useState([]);
  

  /*const handleAgeValidation = (index, birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
  
    const isChild = index >= reservation.adults;
    const isAdult = !isChild;
  
    if (isChild) {
      if (age < minAgeChild || age > maxAgeChild) {
        alert(`The age for a child should be between ${minAgeChild} and ${maxAgeChild}. Current age: ${age}`);
        const updatedGuests = [...guests];
        updatedGuests[index].customer.birth_date = "";
        setGuests(updatedGuests);
      }
    } else if (isAdult) {
      if (age <= maxAgeChild) {
        alert(`The customer is too young to be considered an adult. Current age: ${age}`);
        const updatedGuests = [...guests];
        updatedGuests[index].customer.birth_date = "";
        setGuests(updatedGuests);
      }
    }
  };*/
  



useEffect(() => {
  const fetchVipCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
        params: { business_unit_id: formData.business_unit_id || 1 } // puedes adaptarlo si lo tienes como prop
      });

      const raw = response.data.vip_categories;
      if (raw) {
        const parsed = raw.split(",").map(c => c.trim()).filter(Boolean);
        setVipCategories(parsed);
      }
    } catch (error) {
      console.error("❌ Error fetching VIP categories:", error);
    }
  };

  fetchVipCategories();
}, [formData.business_unit_id]);

useEffect(() => {
  const fetchHotelSettings = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
        params: { business_unit_id: formData.business_unit_id || 1 }
      });

      const { vip_categories, max_age_baby, max_age_child } = response.data;

      if (vip_categories) {
        const parsed = vip_categories.split(",").map(c => c.trim()).filter(Boolean);
        setVipCategories(parsed);
      }

      if (max_age_baby !== undefined) setMaxAgeBaby(max_age_baby);
      if (max_age_child !== undefined) setMaxAgeChild(max_age_child);

    } catch (error) {
      console.error("❌ Error fetching hotel settings:", error);
    }
  };

  fetchHotelSettings();
}, [formData.business_unit_id]);

useEffect(() => {
  if (!formData.birth_date || maxAgeChild === null || minAgeChild === null) return;

  const birthDate = new Date(formData.birth_date);
  const today = new Date();
  const ageInMs = today - birthDate;
  const ageInYears = ageInMs / (1000 * 60 * 60 * 24 * 365.25);
  const ageRounded = Math.floor(ageInYears);

  console.log("📅 Birth date seleccionada:", formData.birth_date);
  console.log("🔢 Edad calculada:", ageRounded);

  // Determinamos si el huésped es adulto o niño según la edad y las configuraciones.
  const isAdult = ageRounded >= parseInt(maxAgeChild, 10); // Si la edad es mayor o igual a la edad máxima de niño, es un adulto.

  // Validación para adultos: debe ser mayor que maxAgeChild
  if (isAdult && ageRounded <= parseInt(maxAgeChild, 10)) {
    alert(t("❌ This customer is too young to be considered an adult.\nCurrent age: {age} years", { age: ageRounded }));
    handleChange({ target: { name: "birth_date", value: "" } });
  }
  // Validación para niños: debe estar entre minAgeChild y maxAgeChild
  else if (!isAdult && (ageRounded < parseInt(minAgeChild, 10) || ageRounded > parseInt(maxAgeChild, 10))) {
    alert(t("❌ The age for a child must be between {min} and {max}.\nCurrent age: {age}", { min: minAgeChild, max: maxAgeChild, age: ageRounded }));
    handleChange({ target: { name: "birth_date", value: "" } });
  }
}, [formData.birth_date, maxAgeChild, minAgeChild, t]);



  return (
    <div className={`customer-form${appearance === "pilcore" ? " customer-form--pilcore" : ""}`}>
      <div className="row">
            <div className="col-md-2 mb-3">
              <label className="form-label">{t("Gender")} <RequiredMark fieldName="gender" /> </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required={isFieldRequired("gender")}
                className="form-select form-select-sm p-2"
              >
                <option value="">{t("Select")}</option>
                <option value="male">{t("Male")}</option>
                <option value="female">{t("Female")}</option>
                <option value="other">{t("Other")}</option>
                <option value="undisclosed">{t("Undisclosed")}</option>
              </select>
            </div>

            <div className="col-md-2 mb-3">
              <label className="form-label">{t("Salutation")} <RequiredMark fieldName="salutation" /></label>
              <select
                name="salutation"
                value={formData.salutation}
                onChange={handleChange}
                required={isFieldRequired("salutation")}
                className="form-select form-select-sm p-2"
              >
                <option value="">{t("Select")}</option>
                <option value="Mr">{t("Mr")}</option>
                <option value="Mrs">{t("Mrs")}</option>
                <option value="Ms">{t("Ms")}</option>
                <option value="Mx">{t("Mx")}</option>
                <option value="None">{t("None")}</option>
              </select>
            </div>

            <div className="col-md-4 mb-3">
            <label className="form-label">
              {t("First Name")} <RequiredMark fieldName="first_name" />
            </label>

              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required={isFieldRequired("first_name")}
                className="form-control form-control-sm p-2"
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">{t("Middle Name")} <RequiredMark fieldName="middle_name" /></label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
                required={isFieldRequired("middle_name")}
                className="form-control form-control-sm p-2"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
            <label className="form-label">
              {t("Last Name")} <RequiredMark fieldName="last_name" />
            </label>

              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required={isFieldRequired("last_name")}
                className="form-control form-control-sm p-2"
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">{t("Second Last Name")} <RequiredMark fieldName="second_last_name" /></label>
              <input
                type="text"
                name="second_last_name"
                value={formData.second_last_name}
                onChange={handleChange}
                required={isFieldRequired("second_last_name")}
                className="form-control form-control-sm p-2"
              />
            </div>

            <div className="col-md-2 mb-3">
            <label className="form-label">
              {t("ID Type")} <RequiredMark fieldName="document_type" />
            </label>
            <select
              name="document_type"
              value={formData.document_type}
              onChange={handleChange}
              required={isFieldRequired("document_type")}
              className="form-select form-select-sm p-2"
            >
              <option value="">{t("Select")}</option>
              <option value="passport">{t("Passport")}</option>
              <option value="id">{t("ID Card")}</option>
              <option value="driving_license">{t("Driving License")}</option>
            </select>

            </div>

            <div className="col-md-2 mb-3">
            <label className="form-label">
              {t("ID Number")} <RequiredMark fieldName="document_number" />
            </label>
            <input
              type="text"
              name="document_number"
              value={formData.document_number}
              onChange={handleChange}
              required={isFieldRequired("document_number")}
              className="form-control form-control-sm p-2"
            />

            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
            <label className="form-label">
              {t("Email")} <RequiredMark fieldName="email" />
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required={isFieldRequired("email")}
              className="form-control form-control-sm p-2"
            />

            </div>

            <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("Phone")} <RequiredMark fieldName="phone" />
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required={isFieldRequired("phone")}
              className="form-control form-control-sm p-2"
            />

            </div>

            <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("Birth Date")} <RequiredMark fieldName="birth_date" />
            </label>
            <input 
              type="date" 
              name="birth_date" 
              value={formData.birth_date} 
              onChange={handleChange}
              required={isFieldRequired("birth_date")}
              onBlur={(e) => {
                if (typeof window !== "undefined" && typeof window.handleExternalAgeValidation === "function") {
                  window.handleExternalAgeValidation(0, e.target.value); // 0 como índice por defecto
                }
              }}
              
                
                className="form-control form-control-sm p-2" 
              />

            </div>

          </div>

          <div className="row">
            <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("Address")} <RequiredMark fieldName="address" />
            </label>

              <input type="text" name="address" value={formData.address} onChange={handleChange} required={isFieldRequired("address")} className="form-control form-control-sm p-2" />
            </div>

            <div className="col-md-2 mb-3">
            <label className="form-label">
              {t("Postal Code")} <RequiredMark fieldName="postal_code" />
            </label>

              <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} required={isFieldRequired("postal_code")} className="form-control form-control-sm p-2" />
            </div>
                    
            <div className="col-md-3 mb-3">
            <label className="form-label">
              {t("City")} <RequiredMark fieldName="city" />
            </label>

              <input type="text" name="city" value={formData.city} onChange={handleChange} required={isFieldRequired("city")} className="form-control form-control-sm p-2" />
            </div>

            <div className="col-md-4 mb-3">
            <label className="form-label">
              {t("Country")} <RequiredMark fieldName="country" />
            </label>

              <Select
                {...selectLabels}
                name="country"
                options={countryList.map((c) => ({ value: c, label: getGuestCountryLabel(c, languageId) })).sort((a, b) => a.label.localeCompare(b.label, languageId))}
                value={formData.country ? { value: formData.country, label: getGuestCountryLabel(formData.country, languageId) } : null}
                onChange={handleSelectChange}
                placeholder={t("Select")}
                isClearable
                isDisabled={disabled}
                required={isFieldRequired("country")}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition={selectMenuPortalTarget ? "fixed" : "absolute"}
                styles={customerSelectStyles || {
                    control: (base) => ({ ...base, color: "#000" }),
                    menu: (base) => ({ ...base, color: "#000" }),
                    singleValue: (base) => ({ ...base, color: "#000" }),
                    option: (base, state) => ({
                    ...base,
                    color: "#000",
                    backgroundColor: state.isFocused ? "#e2e6ea" : "#fff",
                    cursor: "pointer",
                    }),
                    placeholder: (base) => ({ ...base, color: "#555" }),
                }}
                />


            </div> {/* fin de col-md-2 con Country */}

            <div className="col-md-2 mb-3">
              <label className="form-label">{t("Is VIP")} <RequiredMark fieldName="is_vip" /></label>
              <Select
                {...selectLabels}
                name="is_vip"
                value={
                  formData.is_vip
                    ? { value: "true", label: t("Yes") }
                    : { value: "false", label: t("No") }
                }
                onChange={(selectedOption) =>
                  handleChange({
                    target: { name: "is_vip", value: selectedOption.value === "true" }
                  })
                }
                options={[
                  { value: "false", label: t("No") },
                  { value: "true", label: t("Yes") }
                ]}
                isDisabled={disabled}
                required={isFieldRequired("is_vip")}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition={selectMenuPortalTarget ? "fixed" : "absolute"}
                styles={customerSelectStyles || {
                  control: (base) => ({ ...base, fontSize: "0.8rem", color: "#000" }),
                  menu: (base) => ({ ...base, fontSize: "0.8rem", color: "#000" }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: "0.8rem",
                    color: "#000",
                    backgroundColor: state.isFocused ? "#e2e6ea" : "#fff",
                    cursor: "pointer"
                  }),
                  singleValue: (base) => ({ ...base, fontSize: "0.8rem", color: "#000" }),
                  placeholder: (base) => ({ ...base, fontSize: "0.8rem", color: "#555" })
                }}
              />

              <label className="form-label mt-2">{t("Level")} <RequiredMark fieldName="vip_level" /></label>
                <Select
                {...selectLabels}
                  name="vip_level"
                  value={
                    formData.vip_level
                      ? { value: formData.vip_level, label: `${formData.vip_level}` }
                      : null
                  }
                  onChange={(selectedOption) => {
                    console.log("➡️ Nuevo VIP Level:", selectedOption?.value); // Agregado para debug
                    handleChange({
                      target: {
                        name: "vip_level",
                        value: selectedOption?.value || ""
                      }
                    });
                  }}
                  
                  
                  options={vipCategories.map((cat) => ({
                    value: cat,
                    label: cat
                  }))}
                  
                  isDisabled={disabled || !formData.is_vip}
                  required={isFieldRequired("vip_level")}
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition={selectMenuPortalTarget ? "fixed" : "absolute"}
                  styles={customerSelectStyles || {
                    control: (base) => ({ ...base, fontSize: "0.8rem", color: "#000" }),
                    menu: (base) => ({ ...base, fontSize: "0.8rem", color: "#000" }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: "0.8rem",
                      color: "#000",
                      backgroundColor: state.isFocused ? "#e2e6ea" : "#fff",
                      cursor: "pointer"
                    }),
                    singleValue: (base) => ({ ...base, fontSize: "0.8rem", color: "#000" }),
                    placeholder: (base) => ({ ...base, fontSize: "0.8rem", color: "#555" })
                  }}
                />

            </div>



            
            <div className="col-md-3 mb-3">
              <label className="form-label d-flex justify-content-between align-items-center">
                <span>
                  {t("Photo URL")} <RequiredMark fieldName="photo_url" />
                  <span
                    className="text-muted"
                    style={{ fontSize: "0.75rem", marginLeft: "5px" }}
                    title={t("To add a valid photo:\\n1. Click 'Search in Internet'\\n2. Right-click the image\\n3. Open image in new tab\\n4. Copy that URL\\n5. Paste it here")}
                  >
                    ⓘ
                  </span>
                </span>

                {formData.first_name || formData.last_name ? (
                  <a
                  href={`https://duckduckgo.com/?q=${encodeURIComponent(
                    [formData.first_name, formData.middle_name, formData.last_name, formData.second_last_name]
                      .filter(Boolean)
                      .join(" ")
                  )}&iax=images&ia=images`}
                  
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-light"
                  >
                    {t("Search in Internet")}
                  </a>
                ) : (
                  <span className="text-muted" style={{ fontSize: "0.8rem" }}>{t("Enter a name")}</span>
                )}
              </label>

              <input
                type="text"
                name="photo_url"
                value={formData.photo_url}
                onChange={handleChange}
                required={isFieldRequired("photo_url")}
                className="form-control form-control-sm p-2"
                disabled={!formData.is_vip && !isFieldRequired("photo_url")}
                title={t("How to insert a valid photo URL:\n1. Click 'Search in Internet'\n2. Find an image\n3. Right-click\n4. Open image in new tab\n5. Copy that URL and paste here")}
                style={appearance === "pilcore" ? undefined : {
                  backgroundColor: !formData.is_vip && !isFieldRequired("photo_url") ? "#333" : undefined,
                  color: !formData.is_vip && !isFieldRequired("photo_url") ? "#888" : undefined
                }}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">
                {t("Customer Description")} <RequiredMark fieldName="customer_description" />
              </label>
              <textarea
                name="customer_description"
                value={formData.customer_description}
                onChange={handleChange}
                rows="4" // ← antes estaba en 1
                className="form-control form-control-sm p-2"
                placeholder={formData.is_vip ? t("Required for VIPs") : t("Optional notes about this customer")}
                required={formData.is_vip || isFieldRequired("customer_description")}
              />

            </div>


            <div className="col-md-4 mb-3 text-center">
            <img
                src={formData.photo_url || "/default-avatar.png"}
                alt={t("Customer")}
                className="customer-form-avatar"
                style={{
                    maxWidth: "150px",
                    maxHeight: "150px",
                    borderRadius: "10px",
                    border: "2px solid #888",
                    objectFit: "cover"
                }}
                onError={(e) => {
                    if (e.target.src !== window.location.origin + "/default-avatar.png") {
                    e.target.src = "/default-avatar.png";
                    }
                }}
                />
            </div>
          </div>
          
    </div>
  );
};

export default CustomerForm;
